/**
 * Component Name: InvoiceChart
 * Description: A chart component that visualizes invoice data using bar or pie charts, supporting drill-down for projects, sections, POs, suppliers, months, or quarters.
 * Props:
 * - filteredInvoices (array): List of filtered invoice objects to display.
 * - projects (array): List of project objects for mapping project IDs to names.
 * - selectedGroup (string): The grouping type ('project', 'section', 'po', 'supplier', 'month', 'quarter', 'none').
 * - chartDrillDownState (object, optional): State for chart drill-down (type, id, label, etc.).
 * - subTable (object, optional): Sub-table filter data (type, id, stageId, etc.).
 * - onChartBarClick (function): Callback for handling chart bar clicks to trigger drill-down or modal.
 * Feature: Displays invoice data as bar or pie charts based on selected grouping, supports interactive drill-down for detailed views, and handles dynamic resizing for responsiveness.
 */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import moment from 'moment';
import idx from 'idx';
import { getQuarterCalculator, getQuarterFromDate as fallbackGetQuarterFromDate } from '../../routes/suppliers/utils/finance-quarter';
import { useDispatch, useSelector } from 'react-redux';
import { getGlobalSettingReducer } from '../../actions/global-setting.action';
import { FILTER_KEYS_VALUES, INVOICE_CHART_BACK_BUTTON_LABELS, INVOICE_CHART_DRILL_DOWN_STATE, INVOICE_CHART_LABELS, INVOICE_CHART_TOOLTIPS, INVOICE_DATE_FORMAT, INVOICE_ERROR_MESSAGES, INVOICE_GROUPING_VALUES, INVOICE_MONTH_FORMAT, INVOICE_STATUS } from '../../util/enums/invoice-management';
import { GSTORVAT } from 'src/util/enums';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, ChartTooltip, Legend);

// Color palette for consistent charts
const chartColors = {
    project: {
        background: ['#36A2EB', '#FF6384', '#FFCE56'],
        border: ['#2A7BBF', '#D32F2F', '#FBC02D'],
    },
    section: {
        background: ['#FF6384', '#36A2EB', '#FFCE56'],
        border: ['#D32F2F', '#2A7BBF', '#FBC02D'],
    },
    supplier: {
        background: ['#8A2BE2', '#DC143C', '#20B2AA'],
        border: ['#6A0DAD', '#B20000', '#128C8C'],
    },
    month: {
        background: ['#FFC0CB', '#ADD8E6', '#90EE90'],
        border: ['#FF69B4', '#6A5ACD', '#32CD32'],
    },
    quarter: {
        background: ['#AEC6CF', '#77DD77', '#FFB347'],
        border: ['#6D90AA', '#4CAF50', '#FF8C00'],
    },
    po: {
        background: ['#FF9800', '#4CAF50', '#2196F3', '#FF4081', '#7C4DFF', '#00BCD4'],
        border: ['#E65100', '#388E3C', '#1976D2', '#F50057', '#6200EA', '#0097A7'],
    },
    status: {
        background: ['#166534', '#92400e', '#991b1b', '#64748b'],
        border: ['#dcfce7', '#fef3c7', '#fee2e2', '#e2e8f0'],
    }
};

// Utility to generate a random color for pie charts
const dynamicColors = () => {
    const r = Math.floor(Math.random() * 200) + 50;
    const g = Math.floor(Math.random() * 200) + 50;
    const b = Math.floor(Math.random() * 200) + 50;
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
};

const InvoiceChart = ({
    filteredInvoices,
    projects,
    selectedGroup,
    chartDrillDownState,
    subTable,
    onChartBarClick
}) => {
    const chartContainerRef = useRef(null);
    const dispatch = useDispatch();
    // Get the entire globalSetting state, specifically finance_tax
    const financeSettings = useSelector(state => state.globalSetting?.finance_tax || {});
    const [quarterCalculator, setQuarterCalculator] = useState(() => fallbackGetQuarterFromDate); // Initialize with fallback
    let labels = [];
    let data = [];
    let chartTitle = INVOICE_CHART_LABELS.INVOICE_AMOUNTS;
    let backgroundColor = [];
    let borderColor = [];
    let currentChartType = 'bar';
    const projectMap = new Map();
    const supplierMap = new Map();
    const monthMap = new Map();
    const quarterMap = new Map();
    const poMap = new Map();
    const sectionMap = new Map();

    // Load global settings on component mount if not already available
    useEffect(() => {
        // Only dispatch if finance_tax is empty or not yet loaded
        if ((!financeSettings || Object.keys(financeSettings).length === 0) && selectedGroup === INVOICE_GROUPING_VALUES.PO) {
            dispatch(getGlobalSettingReducer('FINANCE_TAX'));
        }
    }, [financeSettings]); // Re-run financeSettings change

    // Update quarterCalculator when financeSettings change
    useEffect(() => {
        if (financeSettings && Object.keys(financeSettings).length > 0) {
            const calculator = getQuarterCalculator(financeSettings);
            setQuarterCalculator(() => calculator);
        } else {
            setQuarterCalculator(() => fallbackGetQuarterFromDate);
        }
    }, [financeSettings]); // Re-run when financeSettings from Redux change

    // Resize observer effect
    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.target === container) {
                    const height = entry.contentRect.height;
                    if (height !== container.style.height) {
                        container.style.height = `${height}px`;
                    }
                }
            }
        });

        return () => resizeObserver.disconnect(); // Cleanup
    }, []);

    // Chart info calculation - moved before conditional return
    const chartInfo = useMemo(() => {
        try {

            let dataToProcess = filteredInvoices;

            if (subTable) {
                dataToProcess = filteredInvoices.filter((inv) => {
                    try {
                        if (subTable.type === INVOICE_GROUPING_VALUES.PO) {
                            return subTable.stageId ? inv.stageId === subTable.stageId : inv.projectId === subTable.projectId;
                        } else if (subTable.type === INVOICE_GROUPING_VALUES.INVOICES) {
                            return idx(inv, _ => inv.PurchaseOrders.id) === subTable.id;
                        } else if (subTable.type === INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES) {
                            return inv.PurchaseOrders?.vendorId === subTable.id;
                        } else if (subTable.type === INVOICE_GROUPING_VALUES.MONTH_INVOICES) {
                            if (subTable.id === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) {
                                const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                                return inv.issueDate === INVOICE_STATUS.NA || !issueDate.isValid();
                            } else {
                                const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                                return issueDate.isValid() && issueDate.format(INVOICE_MONTH_FORMAT) === subTable.id;
                            }
                        } else if (subTable.type === INVOICE_GROUPING_VALUES.QUARTER_INVOICES) {
                            if (subTable.id === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) {
                                const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                                return inv.issueDate === INVOICE_STATUS.NA || !issueDate.isValid();
                            } else {
                                const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                                if (issueDate.isValid()) {
                                    const quarterInfo = quarterCalculator(issueDate);
                                    return quarterInfo.quarterKey === subTable.id;
                                }
                                return false;
                            }
                        }
                        return true;
                    } catch (error) {
                        console.error('Error filtering invoice for subTable:', error);
                        return false;
                    }
                });
            }

            if (chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS) {
                chartTitle = `Invoices for ${chartDrillDownState.label || 'Selected Item'}`;
                currentChartType = 'pie';
                dataToProcess.forEach(inv => {
                    labels.push(inv.invoiceNumber || INVOICE_STATUS.NA);
                    data.push(roundValue(toDecimal(inv.totalAmount || 0)));
                });

                backgroundColor = labels.map(() => dynamicColors());
                borderColor = backgroundColor.map(color => color.replace('0.8', '1'));
            } else if (chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS) {
                dataToProcess.forEach(inv => {
                    const poId = idx(inv, _ => inv.PurchaseOrders.id) || INVOICE_STATUS.NA;
                    const amount = toDecimal(inv.totalAmount || 0);
                    if (!poMap.has(poId)) {
                        poMap.set(poId, {
                            totalAmount: toDecimal(0),
                            invoiceCount: 0,
                            totalSubtotal: toDecimal(0),
                            poData: inv.PurchaseOrders,
                            invoices: [],
                            projectId: inv.projectId,
                            stageId: inv.stageId
                        });
                    }
                    const poAgg = poMap.get(poId);
                    poAgg.totalAmount = poAgg.totalAmount.plus(amount);
                    poAgg.invoiceCount += 1;
                    poAgg.totalSubtotal = poAgg.totalSubtotal.plus(toDecimal(inv.subTotal || 0));
                    poAgg.invoices.push(inv);
                });
                chartTitle = `Purchase Orders for ${chartDrillDownState.label || 'Selected Item'}`;
                labels = Array.from(poMap.keys());
                data = Array.from(poMap.values()).map(val => roundValue(val.totalAmount));
                backgroundColor = chartColors.po.background;
                borderColor = chartColors.po.border;
                currentChartType = 'bar';
            } else {
                switch (selectedGroup) {
                    case INVOICE_GROUPING_VALUES.PROJECT:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_PROJECT;
                        dataToProcess.forEach(inv => {
                            const projectId = inv.projectId;
                            const projectName = projects.find(p => p.id === projectId)?.name || INVOICE_ERROR_MESSAGES.UNKNOWN_PROJECT;
                            const amount = toDecimal(inv.totalAmount || 0);
                            if (!projectMap.has(projectId)) {
                                projectMap.set(projectId, {
                                    name: projectName,
                                    totalAmount: toDecimal(0),
                                    totalSubtotal: toDecimal(0),
                                    invoiceCount: 0,
                                    poCount: new Set(),
                                    statuses: new Set()
                                });
                            }
                            const projectAgg = projectMap.get(projectId);
                            projectAgg.totalAmount = projectAgg.totalAmount.plus(amount);
                            projectAgg.totalSubtotal = projectAgg.totalSubtotal.plus(toDecimal(inv.subTotal || 0));
                            projectAgg.invoiceCount += 1;
                            if (idx(inv, _ => inv.PurchaseOrders.id)) {
                                projectAgg.poCount.add(idx(inv, _ => inv.PurchaseOrders.id));
                            }
                            projectAgg.statuses.add(inv.status);
                        });
                        labels = Array.from(projectMap.keys()).map(id => projectMap.get(id).name || INVOICE_ERROR_MESSAGES.UNKNOWN_PROJECT);
                        data = Array.from(projectMap.values()).map(val => roundValue(val.totalAmount));
                        backgroundColor = chartColors.project.background;
                        borderColor = chartColors.project.border;
                        break;
                    case INVOICE_GROUPING_VALUES.SECTION:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_SECTION;
                        dataToProcess.forEach(inv => {
                            const sectionKey = `${inv.projectId}-${inv.stageId}`;
                            const sectionName = inv.sectionHierarchy || INVOICE_STATUS.NA;
                            const amount = toDecimal(inv.totalAmount || 0);
                            if (!sectionMap.has(sectionKey)) {
                                sectionMap.set(sectionKey, {
                                    sectionKey,
                                    sectionName,
                                    totalAmount: toDecimal(0),
                                    totalSubtotal: toDecimal(0),
                                    invoiceCount: 0,
                                    poCount: new Set(),
                                    statuses: new Set(),
                                    projectId: inv.projectId,
                                    stageId: inv.stageId
                                });
                            }
                            const sectionAgg = sectionMap.get(sectionKey);
                            sectionAgg.totalAmount = sectionAgg.totalAmount.plus(amount);
                            sectionAgg.totalSubtotal = sectionAgg.totalSubtotal.plus(toDecimal(inv.subTotal || 0));
                            sectionAgg.invoiceCount += 1;
                            if (idx(inv, _ => inv.PurchaseOrders.id)) {
                                sectionAgg.poCount.add(idx(inv, _ => inv.PurchaseOrders.id));
                            }
                            sectionAgg.statuses.add(inv.status);
                        });
                        labels = Array.from(sectionMap.values()).map(section => section.sectionName);
                        data = Array.from(sectionMap.values()).map(val => roundValue(val.totalAmount));
                        backgroundColor = chartColors.section.background;
                        borderColor = chartColors.section.border;
                        break;
                    case INVOICE_GROUPING_VALUES.SUPPLIER:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_SUPPLIER;
                        dataToProcess.forEach(inv => {
                            const supplierId = inv.PurchaseOrders?.vendorId;
                            const supplierName = inv.supplier || INVOICE_ERROR_MESSAGES.UNKNOWN_SUPPLIER;
                            const amount = toDecimal(inv.totalAmount || 0);

                            if (supplierId) {
                                if (!supplierMap.has(supplierId)) {
                                    supplierMap.set(supplierId, {
                                        supplierId,
                                        supplierName,
                                        totalAmount: toDecimal(0),
                                        totalSubtotal: toDecimal(0),
                                        invoiceCount: 0,
                                        poCount: new Set()
                                    });
                                }
                                const supplierData = supplierMap.get(supplierId);
                                supplierData.totalAmount = supplierData.totalAmount.plus(amount);
                                supplierData.totalSubtotal = supplierData.totalSubtotal.plus(toDecimal(inv.subTotal || 0));
                                supplierData.invoiceCount += 1;
                                if (inv.PurchaseOrders?.id) {
                                    supplierData.poCount.add(inv.PurchaseOrders.id);
                                }
                            }
                        });
                        labels = Array.from(supplierMap.values()).map(supplier => supplier.supplierName);
                        data = Array.from(supplierMap.values()).map(val => roundValue(val.totalAmount));
                        backgroundColor = chartColors.supplier.background;
                        borderColor = chartColors.supplier.border;
                        break;
                    case INVOICE_GROUPING_VALUES.MONTH:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_MONTH;
                        dataToProcess.forEach(inv => {
                            const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                            if (inv.issueDate === INVOICE_STATUS.NA || !issueDate.isValid()) {
                                // Handle empty issue date case
                                const monthKey = FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE;
                                const monthLabel = 'Empty Issue Date';
                                const currentData = monthMap.get(monthKey) || { label: monthLabel, amount: toDecimal(0) };
                                monthMap.set(monthKey, {
                                    label: monthLabel,
                                    amount: currentData.amount.plus(toDecimal(inv.totalAmount || 0))
                                });
                            } else {
                                const monthKey = issueDate.format(INVOICE_MONTH_FORMAT);
                                const monthLabel = issueDate.format('MMMM YYYY');
                                const currentData = monthMap.get(monthKey) || { label: monthLabel, amount: toDecimal(0) };
                                monthMap.set(monthKey, {
                                    label: monthLabel,
                                    amount: currentData.amount.plus(toDecimal(inv.totalAmount || 0))
                                });
                            }
                        });
                        const sortedMonths = Array.from(monthMap.entries()).sort(([keyA], [keyB]) => {
                            // Place 'empty-issue-date' at the end
                            if (keyA === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) return 1;
                            if (keyB === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) return -1;
                            return keyA.localeCompare(keyB);
                        });
                        labels = sortedMonths.map(([, { label }]) => label);
                        data = sortedMonths.map(([, { amount }]) => roundValue(amount));
                        backgroundColor = chartColors.month.background;
                        borderColor = chartColors.month.border;
                        break;
                    case INVOICE_GROUPING_VALUES.QUARTER:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_QUARTER;
                        dataToProcess.forEach(inv => {
                            const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                            if (inv.issueDate === 'N/A' || !issueDate.isValid()) {
                                // Handle empty issue date case
                                const quarterKey = FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE;
                                const quarterLabel = 'Empty Issue Date';
                                const currentData = quarterMap.get(quarterKey) || { label: quarterLabel, amount: toDecimal(0) };
                                quarterMap.set(quarterKey, {
                                    label: quarterLabel,
                                    amount: currentData.amount.plus(toDecimal(inv.totalAmount || 0))
                                });
                            } else {
                                try {
                                    // Use the quarterCalculator function obtained from the state
                                    const quarterInfo = quarterCalculator(issueDate);
                                    const currentData = quarterMap.get(quarterInfo.quarterKey) || {
                                        label: quarterInfo.quarterLabel,
                                        amount: toDecimal(0)
                                    };
                                    quarterMap.set(quarterInfo.quarterKey, {
                                        label: quarterInfo.quarterLabel,
                                        amount: currentData.amount.plus(toDecimal(inv.totalAmount || 0))
                                    });
                                } catch (error) {
                                    console.error('Error calculating quarter for invoice:', inv.invoiceId, error);
                                }
                            }
                        });
                        const sortedQuarters = Array.from(quarterMap.entries()).sort(([keyA], [keyB]) => {
                            // Place 'empty-issue-date' at the end
                            if (keyA === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) return 1;
                            if (keyB === FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE) return -1;
                            return keyA.localeCompare(keyB);
                        });
                        labels = sortedQuarters.map(([, { label }]) => label);
                        data = sortedQuarters.map(([, { amount }]) => roundValue(amount));
                        backgroundColor = chartColors.quarter.background;
                        borderColor = chartColors.quarter.border;
                        break;
                    case INVOICE_GROUPING_VALUES.PO:
                        chartTitle = INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT_BY_PO;
                        dataToProcess.forEach(inv => {
                            /* f_id is the PO number with project prefix and Suffix if null then default to po Number*/
                            const poId = idx(inv, _ => inv.PurchaseOrders.f_id) || idx(inv, _ => inv.PurchaseOrders.id) || INVOICE_STATUS.NA;
                            const amount = toDecimal(inv.totalAmount || 0);
                            if (!poMap.has(poId)) {
                                poMap.set(poId, {
                                    totalAmount: toDecimal(0),
                                    totalSubtotal: toDecimal(0),
                                    invoiceCount: 0,
                                    poData: inv.PurchaseOrders,
                                    projectId: inv.projectId,
                                    stageId: inv.stageId
                                });
                            }
                            const poAgg = poMap.get(poId);
                            poAgg.totalAmount = poAgg.totalAmount.plus(amount);
                            poAgg.totalSubtotal = poAgg.totalSubtotal.plus(toDecimal(inv.subTotal || 0));
                            poAgg.invoiceCount += 1;
                        });
                        labels = Array.from(poMap.keys());
                        data = Array.from(poMap.values()).map(val => roundValue(val.totalAmount));
                        backgroundColor = chartColors.po.background;
                        borderColor = chartColors.po.border;
                        break;
                    case INVOICE_GROUPING_VALUES.NONE:
                    default:
                        chartTitle = INVOICE_CHART_LABELS.INVOICE_STATUS_DISTRIBUTION;
                        const statusMap = new Map();
                        dataToProcess.forEach(inv => {
                            const status = idx(inv, _ => inv.PurchaseOrders.invoicestatus) || INVOICE_STATUS.NA;
                            const amount = toDecimal(inv.totalAmount || 0);
                            const currentTotal = statusMap.get(status) || toDecimal(0);
                            statusMap.set(status, currentTotal.plus(amount));
                        });
                        labels = Array.from(statusMap.keys());
                        data = Array.from(statusMap.values()).map(val => roundValue(val));
                        currentChartType = 'pie';
                        backgroundColor = chartColors.status.background;
                        borderColor = chartColors.status.border;
                        break;
                }
            }

            return {
                labels,
                datasets: [{
                    label: INVOICE_CHART_LABELS.TOTAL_INVOICE_AMOUNT,
                    data,
                    backgroundColor,
                    borderColor,
                    borderWidth: 1,
                }],
                chartTitle,
                currentChartType,
                groupedDataMap: chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS ? poMap :
                    selectedGroup === INVOICE_GROUPING_VALUES.PROJECT ? projectMap :
                        selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? sectionMap :
                            selectedGroup === INVOICE_GROUPING_VALUES.PO ? poMap : null
            };
        } catch (error) {
            console.error('Error in useMemo for InvoiceChart:', error);
            return {
                labels: [],
                datasets: [],
                chartTitle: INVOICE_ERROR_MESSAGES.ERROR_IN_CHART,
                currentChartType: 'bar',
                groupedDataMap: new Map()
            };
        }
    }, [filteredInvoices, projects, selectedGroup, chartDrillDownState, subTable, quarterCalculator]);

    let dataToProcess = filteredInvoices;

    const handleChartClick = (event, elements) => {
        if (!elements.length) return;
        const { index } = elements[0];
        const clickedLabel = chartInfo.labels[index];


        if (!chartDrillDownState || chartDrillDownState.type === INVOICE_CHART_DRILL_DOWN_STATE.GROUP) {
            switch (selectedGroup) {
                case INVOICE_GROUPING_VALUES.PROJECT:
                    const clickedProject = projects.find(p => p.name === clickedLabel);
                    if (clickedProject) {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.PROJECT_DETAILS, clickedProject.id, clickedProject.name, clickedProject.id, null, selectedGroup);
                    }
                    break;
                case INVOICE_GROUPING_VALUES.SECTION:
                    // Find section by name in the sectionMap values
                    const clickedSectionData = Array.from(sectionMap.values()).find(section => section.sectionName === clickedLabel);
                    if (clickedSectionData) {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.SECTION, clickedSectionData.stageId, clickedLabel, clickedSectionData.projectId, clickedSectionData.stageId, selectedGroup);
                    }
                    break;
                case INVOICE_GROUPING_VALUES.PO:
                    const poData = chartInfo.groupedDataMap.get(clickedLabel);
                    if (poData) {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.PO_INVOICES, clickedLabel, clickedLabel, poData.projectId, poData.stageId, selectedGroup);
                    }
                    break;
                case INVOICE_GROUPING_VALUES.SUPPLIER:
                    // Find supplier by name in the supplierMap values
                    const supplierData = Array.from(supplierMap.values()).find(supplier => supplier.supplierName === clickedLabel);
                    if (supplierData) {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.SUPPLIER_INVOICES, supplierData.supplierId, clickedLabel, null, null, selectedGroup);
                    }
                    break;
                case INVOICE_GROUPING_VALUES.MONTH:
                    if (clickedLabel === 'Empty Issue Date') {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.MONTH_INVOICES, FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE, clickedLabel, null, null, selectedGroup);
                    } else {
                        const monthInvoice = dataToProcess.find(inv => moment(inv.issueDate, INVOICE_DATE_FORMAT, true).format('MMMM YYYY') === clickedLabel);
                        if (monthInvoice) {
                            onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.MONTH_INVOICES, moment(monthInvoice.issueDate, INVOICE_DATE_FORMAT, true).format(INVOICE_MONTH_FORMAT), clickedLabel, null, null, selectedGroup);
                        }
                    }
                    break;
                case INVOICE_GROUPING_VALUES.QUARTER:
                    if (clickedLabel === 'Empty Issue Date') {
                        onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.QUARTER_INVOICES, FILTER_KEYS_VALUES.EMPTY_ISSUE_DATE, clickedLabel, null, null, selectedGroup);
                    } else {
                        // Find an invoice that matches the quarter label using the current quarterCalculator
                        const quarterInvoice = dataToProcess.find(inv => {
                            const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true);
                            if (issueDate.isValid()) {
                                const qInfo = quarterCalculator(issueDate);
                                return qInfo.quarterLabel === clickedLabel;
                            }
                            return false;
                        });
                        if (quarterInvoice) {
                            const qInfo = quarterCalculator(moment(quarterInvoice.issueDate, INVOICE_DATE_FORMAT, true));
                            onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.QUARTER_INVOICES, qInfo.quarterKey, clickedLabel, null, null, selectedGroup);
                        } else {
                            console.warn('No quarter invoice found for label:', clickedLabel);
                        }
                    }
                    break;
                case INVOICE_GROUPING_VALUES.NONE:
                    // No drill-down for status pie chart
                    break;
                default:
                    console.warn('Unhandled chart group:', selectedGroup);
                    break;
            }
        } else if (chartDrillDownState.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS) {
            const poData = chartInfo.groupedDataMap.get(clickedLabel);
            if (poData) {
                onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.PO_INVOICES, clickedLabel, clickedLabel, poData.projectId, poData.stageId, chartDrillDownState.originalGroup || selectedGroup);
            }
        } else if (chartDrillDownState.type === INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS) {
            const invoice = dataToProcess.find(inv => inv.invoiceNumber === clickedLabel);
            if (invoice) {
                onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.VIEW_INVOICE_MODAL, invoice.invoiceId, invoice.invoiceNumber);
            }
        }
    };

    const commonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleChartClick,
        interaction: {
            intersect: false,
            axis: 'x',
            mode: 'nearest',
            trigger: 'click',
        },
        plugins: {
            title: {
                display: true,
                text: chartInfo.chartTitle,
                color: 'white',
                font: {
                    size: 18,
                    weight: 'bold'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.89)',
                titleColor: '#000',
                bodyColor: '#000',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 8,
                callbacks: {
                    label: (context) => {
                        const label = context.label || '';
                        const value = context.raw;
                        if (chartInfo.currentChartType === 'pie') {
                            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                            const percentage = total > 0 ? roundValue(toDecimal(value).div(total).mul(100)) : '0.00';
                            return [
                                `${INVOICE_CHART_TOOLTIPS.INVOICE_NUMBER} : ${label}`,
                                `${INVOICE_CHART_TOOLTIPS.TOTAL_AMOUNT}(Inc. ${GSTORVAT}) : ${toUSD(value)}`,
                                `${INVOICE_CHART_TOOLTIPS.SUBTOTAL} : ${toUSD(context.dataset.data[context.dataIndex])}`,
                                `${INVOICE_CHART_TOOLTIPS.PERCENTAGE} : ${percentage}%`
                            ];
                        } else {
                            if (!chartDrillDownState && selectedGroup === INVOICE_GROUPING_VALUES.PROJECT) {
                                // Find project by name in the projectMap values
                                const projectData = Array.from(projectMap.values()).find(project => project.name === label);
                                if (projectData) {
                                    return [
                                        `${INVOICE_CHART_TOOLTIPS.PROJECT} : ${label}`,
                                        `${INVOICE_CHART_TOOLTIPS.TOTAL_AMOUNT}(Inc. ${GSTORVAT}) : ${toUSD(roundValue(projectData.totalAmount))}`,
                                        `${INVOICE_CHART_TOOLTIPS.SUBTOTAL} : ${toUSD(roundValue(projectData.totalSubtotal))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICES} : ${projectData.invoiceCount}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_COUNT} : ${projectData.poCount.size}`
                                    ];
                                }
                            } else if (chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS) {
                                const poAggData = chartInfo.groupedDataMap.get(label);
                                if (poAggData) {
                                    return [
                                        `${INVOICE_CHART_TOOLTIPS.PO_NUMBER} : ${label}`,
                                        // `${INVOICE_CHART_TOOLTIPS.PROCUREMENT_CODE} : ${poAggData.poData?.f_id || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.VENDOR} : ${poAggData.poData?.vendor?.business_name || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_TOTAL} : ${toUSD(toDecimal(poAggData.poData?.total || 0))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_AMOUNT}(Inc. ${GSTORVAT}) : ${toUSD(roundValue(poAggData.totalAmount))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_SUBTOTAL} : ${toUSD(roundValue(poAggData.totalSubtotal))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_COUNT} : ${poAggData.invoiceCount}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_MATERIAL_STATUS} : ${poAggData.poData?.materialstatus || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_STATUS} : ${poAggData.poData?.invoicestatus || INVOICE_STATUS.NA}`
                                    ];
                                }
                            } else if (!chartDrillDownState && selectedGroup === INVOICE_GROUPING_VALUES.SECTION) {
                                // Find section by name in the sectionMap values
                                const sectionData = Array.from(sectionMap.values()).find(section => section.sectionName === label);
                                if (sectionData) {
                                    return [
                                        `${INVOICE_CHART_TOOLTIPS.SECTION} : ${label}`,
                                        `${INVOICE_CHART_TOOLTIPS.TOTAL_AMOUNT}(Inc. ${GSTORVAT}) : ${toUSD(roundValue(sectionData.totalAmount))}`,
                                        `${INVOICE_CHART_TOOLTIPS.SUBTOTAL} : ${toUSD(roundValue(sectionData.totalSubtotal))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICES} : ${sectionData.invoiceCount}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_COUNT} : ${sectionData.poCount.size}`
                                    ];
                                }
                            } else if (!chartDrillDownState && selectedGroup === INVOICE_GROUPING_VALUES.SUPPLIER) {
                                // Find supplier by name in the supplierMap values
                                const supplierData = Array.from(supplierMap.values()).find(supplier => supplier.supplierName === label);
                                if (supplierData) {
                                    return [
                                        `${INVOICE_CHART_TOOLTIPS.SUPPLIER} : ${label}`,
                                        `${INVOICE_CHART_TOOLTIPS.TOTAL_AMOUNT} : ${toUSD(roundValue(supplierData.totalAmount))}`,
                                        `${INVOICE_CHART_TOOLTIPS.SUBTOTAL} : ${toUSD(roundValue(supplierData.totalSubtotal))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICES} : ${supplierData.invoiceCount}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_COUNT} : ${supplierData.poCount.size}`
                                    ];
                                }
                            } else if (!chartDrillDownState && selectedGroup === INVOICE_GROUPING_VALUES.PO) {
                                const poData = chartInfo.groupedDataMap.get(label);
                                if (poData) {
                                    return [
                                        `${INVOICE_CHART_TOOLTIPS.PO_NUMBER} : ${label}`,
                                        // `${INVOICE_CHART_TOOLTIPS.PROCUREMENT_CODE} : ${poData.poData?.f_id || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.VENDOR} : ${poData.poData?.vendor?.business_name || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_TOTAL} : ${toUSD(toDecimal(poData.poData?.total || 0))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_AMOUNT}(Inc. ${GSTORVAT}) : ${toUSD(roundValue(poData.totalAmount))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_SUBTOTAL} : ${toUSD(roundValue(poData.totalSubtotal))}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_COUNT} : ${poData.invoiceCount}`,
                                        `${INVOICE_CHART_TOOLTIPS.PO_MATERIAL_STATUS} : ${poData.poData?.materialstatus || INVOICE_STATUS.NA}`,
                                        `${INVOICE_CHART_TOOLTIPS.INVOICE_STATUS} : ${poData.poData?.invoicestatus || INVOICE_STATUS.NA}`
                                    ];
                                }
                            }
                            return `${context.dataset.label}: ${toUSD(value)}`;
                        }
                    },
                },
            },
        },
    };

    const barChartOptions = {
        ...commonChartOptions,
        maintainAspectRatio: false,
        responsive: true,
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Amount (USD)',
                    color: 'white'
                },
                ticks: {
                    callback: (value) => toUSD(value),
                    color: 'white'
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.2)'
                }
            },
            x: {
                title: {
                    display: true,
                    text: chartInfo.chartTitle.replace('Total Invoice Amount by ', '').replace('Purchase Orders for ', ''),
                    color: 'white'
                },
                ticks: {
                    color: 'white',
                    maxRotation: chartInfo.labels.length > 2 ? 45 : 0,
                    minRotation: 0,
                    autoSkip: false,
                    maxTicksLimit: false,
                    callback: function (value) {
                        const label = this.getLabelForValue(value);
                        return label;
                        // Truncate long labels for better display
                        // return label.length > 15 ? label.substring(0, 12) + '...' : label;
                    }
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.2)'
                }
            },
        },
        plugins: {
            ...commonChartOptions.plugins,
            legend: {
                display: false,
                labels: {
                    color: 'white'
                }
            },
        },
    };

    const pieChartOptions = {
        ...commonChartOptions,
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            ...commonChartOptions.plugins,
            legend: {
                position: 'right',
                labels: {
                    color: 'white',
                    font: {
                        size: 14
                    }
                }
            },
        },
    };

    const handleChartBack = () => {
        if (chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS) {
            const originalGroup = chartDrillDownState.originalGroup || selectedGroup;
            if ([INVOICE_GROUPING_VALUES.PROJECT, INVOICE_GROUPING_VALUES.SECTION, INVOICE_GROUPING_VALUES.PO].includes(originalGroup)) {
                if (originalGroup === INVOICE_GROUPING_VALUES.PROJECT) {
                    onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, chartDrillDownState.projectId, projects.find(p => p.id === chartDrillDownState.projectId)?.name || INVOICE_ERROR_MESSAGES.UNKNOWN_PROJECT, chartDrillDownState.projectId, null, originalGroup);
                } else if (originalGroup === INVOICE_GROUPING_VALUES.SECTION) {
                    onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, chartDrillDownState.stageId, chartDrillDownState.label || INVOICE_ERROR_MESSAGES.UNKNOWN_SECTION, chartDrillDownState.projectId, chartDrillDownState.stageId, originalGroup);
                } else if (originalGroup === INVOICE_GROUPING_VALUES.PO) {
                    onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.GROUP, INVOICE_GROUPING_VALUES.PO, INVOICE_GROUPING_VALUES.PO, null, null, originalGroup);
                }
            } else {
                onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.GROUP, originalGroup, originalGroup); // Go back to the original grouping (month, quarter, supplier etc.)
            }
        } else if (chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS) {
            onChartBarClick(INVOICE_CHART_DRILL_DOWN_STATE.GROUP, chartDrillDownState.originalGroup || selectedGroup, chartDrillDownState.originalGroup);
        }
    };

    return (
        <div
            ref={chartContainerRef}
            className="chart-container mt-3 w-full"
        >
            {(chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS || chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS) && (
                <div className="chart-back-button-container">
                    <button
                        onClick={handleChartBack}
                        className="btn btn-outline-light btn-sm mb-2 rounded-md"
                        title={INVOICE_CHART_BACK_BUTTON_LABELS.BACK_TO_CHART}
                    >
                        {chartDrillDownState?.type === INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS
                            ? ([INVOICE_GROUPING_VALUES.PROJECT, INVOICE_GROUPING_VALUES.SECTION, INVOICE_GROUPING_VALUES.PO].includes(chartDrillDownState.originalGroup) ? INVOICE_CHART_BACK_BUTTON_LABELS.PO_S : INVOICE_CHART_BACK_BUTTON_LABELS.GROUPS)
                            : INVOICE_CHART_BACK_BUTTON_LABELS.GROUPS}
                    </button>
                </div>
            )}
            <div className='canvas-container' data-cy="invoice-chart">
                {chartInfo.labels.length > 0 ? (
                    chartInfo.currentChartType === 'bar' ? (
                        <Bar data={chartInfo} options={barChartOptions} />
                    ) : (
                        <Pie data={chartInfo} options={pieChartOptions} />
                    )
                ) : (
                    <p className="text-white text-center py-4">{INVOICE_ERROR_MESSAGES.NO_CHART_DATA}</p>
                )}
            </div>
        </div>
    );
};

export default InvoiceChart;